import { useState, useCallback, useEffect } from 'react';
import { Link, useParams, useLocation } from '@tanstack/react-router';
import { useLingui } from '@lingui/react';
import clsx from 'clsx';
import logoImg from '../../assets/img/logo.svg';
import logoDark from '../../assets/img/logo-dark.svg';
import ButtonIcon from '../ui/ButtonIcon';
import Icon from '../ui/Icon';
import DocPageIcon from '../Docs/DocPageIcon';
import { useTheme, useMobileNavSidebar } from '../../hooks/useUserPreferences';
import { getAllSections, getDeveloperSections } from '../../services/docs';
import {
  asGroupKey,
  activeGroupFromPath,
  developerVersionFromPath,
  docPageRoute,
  groupExpandsAllSections,
  groupIndexRoute,
  LATEST_DEVELOPER_VERSION,
  type DocGroupKey,
} from '../Docs/docGroupRoutes';
import { useCollapseAnimation } from './useCollapseAnimation';
import DocsGroupFooterNav from './DocsGroupFooterNav';

// Sections are collapsed by default; the active section opens on navigation
// (see below). Groups that opt into expanding everything (see
// groupExpandsAllSections) open all sections instead.
function isDefaultOpen(group: DocGroupKey): boolean {
  return groupExpandsAllSections(group);
}

interface DocsSectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  /** True when the current route is within this section; keeps the header highlighted. */
  isActiveSection?: boolean;
}

export function DocsSection({
  title,
  children,
  isOpen,
  onToggle,
  isActiveSection = false,
}: DocsSectionProps) {
  const { style, innerRef } = useCollapseAnimation(isOpen);

  const toggleOpen = useCallback(() => {
    onToggle(!isOpen);
  }, [isOpen, onToggle]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    }
  };

  return (
    <div className="side-nav-section">
      <div
        className={clsx('docs-nav-section-header', isActiveSection && 'active')}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <span className="docs-nav-section-heading">{title}</span>
        <Icon
          name="arrow-down"
          className={clsx(
            'docs-nav-section-arrow flex-shrink-0 transition-transform duration-[250ms]',
            isOpen ? 'rotate-0' : '-rotate-90',
          )}
        />
      </div>
      <div className={clsx('side-nav-section-content', isOpen && 'show')} style={style}>
        <div ref={innerRef}>
          <ul className="list-reset">{children}</ul>
        </div>
      </div>
    </div>
  );
}

function DocsNavigation() {
  useLingui();
  const { color } = useTheme();
  const { closeMobileNavSidebar } = useMobileNavSidebar();
  const params = useParams({ strict: false }) as { section?: string; slug?: string };
  const pathname = useLocation({ select: (l) => l.pathname });

  // Which group's sections this sidebar shows. Falls back to documentation so a
  // bare /docs (or an unknown path) still renders the original navigation.
  const activeGroup: DocGroupKey = asGroupKey(
    activeGroupFromPath(pathname) ?? 'documentation',
  );
  // The developers sidebar lists the unversioned sections (Overview) plus the
  // latest version's API sections, flat. Which version's page you're on is read
  // from the path (the version is a static URL segment, not a route param).
  const currentVersion = developerVersionFromPath(pathname);
  const sections =
    activeGroup === 'developers'
      ? getDeveloperSections(LATEST_DEVELOPER_VERSION)
      : getAllSections(activeGroup);

  // In-memory open/closed overrides, keyed by section slug across all groups
  // (slugs are disjoint between groups). Effective open state falls back to the
  // group's default when the reader hasn't toggled a section yet. State is kept
  // only for the session, so a refresh resets sections to their defaults while
  // toggles still persist across internal navigation.
  const [sectionStates, setSectionStates] = useState<Record<string, boolean>>({});

  const handleToggle = useCallback((slug: string, isOpen: boolean) => {
    setSectionStates((prev) => ({ ...prev, [slug]: isOpen }));
  }, []);

  // On navigation (hard or internal), expand the section holding the current
  // page — even if the reader collapsed it earlier this session. Runs only when
  // the active section changes, so a section the reader collapses while staying
  // on it stays collapsed.
  useEffect(() => {
    const active = params.section;
    if (!active) return;
    setSectionStates((prev) =>
      prev[active] === true ? prev : { ...prev, [active]: true },
    );
  }, [params.section]);

  const logo = color === 'light' || color === 'sepia' ? logoImg : logoDark;

  // Application management is a non-markdown area shown only in the Developers
  // sidebar (OAuth apps are a Full API concern). Built here so `t` runs at
  // render time. "Your Apps" stays active across the list and the edit pages.
  const appNavItems =
    activeGroup === 'developers'
      ? ([
          {
            to: '/developers/applications',
            icon: 'SquaresFour',
            label: 'Your Apps',
            isActive:
              pathname.startsWith('/developers/applications') &&
              pathname !== '/developers/applications/create' &&
              pathname !== '/developers/applications/claim',
          },
          {
            to: '/developers/applications/create',
            icon: 'PlusCircle',
            label: 'New App',
            isActive: pathname === '/developers/applications/create',
          },
          {
            to: '/developers/applications/claim',
            icon: 'Key',
            label: 'Claim App',
            isActive: pathname === '/developers/applications/claim',
          },
        ] as const)
      : [];

  const isApplicationsRoute = pathname.startsWith('/developers/applications');

  return (
    <nav className="side-nav docs-side-nav" aria-label="Documentation Navigation">
      <div className="logo-container hidden md:block">
        <Link to={groupIndexRoute[activeGroup]} className="block">
          <img src={logo} alt="Instapaper" className="block w-[106px]" />
        </Link>
      </div>

      <div
        className="docs-side-nav-scroller px-2 pt-4 pb-0 md:pt-0"
        data-testid="nav-scroller"
      >
        <div className="mb-4 flex justify-end md:hidden">
          <ButtonIcon
            icon="close"
            title="Close"
            onClick={closeMobileNavSidebar}
            className="text-copy-secondary"
          />
        </div>

        <div className="md:hidden">
          <a href="/" className="side-item pl-3">
            <span>Back to App</span>
          </a>
        </div>

        {sections.map((section) => {
          // A section matches the current route only when both its slug and its
          // version line up — so /developers/v1/simple-api highlights the v1
          // Simple API, not an unversioned section that happens to share a slug.
          const isActiveSection =
            params.section === section.slug && section.version === currentVersion;
          // Active section opens on navigation; the effect above keeps it open
          // across internal navigation, this fallback avoids a flash on first
          // render (hard load) before that effect runs.
          const isOpen =
            sectionStates[section.slug] ??
            (isActiveSection || isDefaultOpen(activeGroup));
          return (
            <DocsSection
              key={`${section.version ?? ''}-${section.slug}`}
              title={section.title}
              isOpen={isOpen}
              onToggle={(open) => handleToggle(section.slug, open)}
              isActiveSection={isActiveSection}
            >
              {section.pages.map((page) => {
                const isActive =
                  params.section === section.slug &&
                  params.slug === page.slug &&
                  section.version === currentVersion;
                return (
                  <li key={page.slug}>
                    <Link
                      to={docPageRoute(activeGroup, section.version)}
                      params={{ section: section.slug, slug: page.slug }}
                      onClick={closeMobileNavSidebar}
                      data-active={isActive ? 'true' : undefined}
                      className={clsx(
                        'side-item docs-nav-page-item gap-2',
                        !isActive && 'transition-all',
                      )}
                    >
                      {page.icon && (
                        <DocPageIcon name={page.icon} className="flex-shrink-0" />
                      )}
                      <span>{page.title}</span>
                    </Link>
                  </li>
                );
              })}
            </DocsSection>
          );
        })}

        {appNavItems.length > 0 && (
          <DocsSection
            title="Applications"
            isOpen={sectionStates['applications'] ?? isApplicationsRoute}
            onToggle={(open) => handleToggle('applications', open)}
            isActiveSection={isApplicationsRoute}
          >
            {appNavItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={closeMobileNavSidebar}
                  data-active={item.isActive ? 'true' : undefined}
                  className={clsx(
                    'side-item docs-nav-page-item gap-2',
                    !item.isActive && 'transition-all',
                  )}
                >
                  <DocPageIcon name={item.icon} className="flex-shrink-0" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </DocsSection>
        )}
      </div>

      <DocsGroupFooterNav />
    </nav>
  );
}

export default DocsNavigation;
